import React, { useState } from 'react'
import { Lock, Plus, X } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import BlockFieldDialog from './BlockFieldDialog'
import { OBJECT_TYPES } from '../types'
import type { CrmObjectType, FieldPermissions } from '../types'

type Props = {
  fieldPermissions: FieldPermissions
  onChange: (objectType: CrmObjectType, deny: string[]) => void
  disabled?: boolean
}

const ObjectTypeCard: React.FC<{
  label: string
  deny: string[]
  onRemove: (field: string) => void
  onManage: () => void
  disabled?: boolean
}> = ({ label, deny, onRemove, onManage, disabled }) => {
  const { currentTheme: t } = useTheme()

  return (
    <div className="rounded-[14px] p-4 flex flex-col gap-3" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13.5px] font-semibold" style={{ color: t.text }}>{label}</p>
        {deny.length > 0 && (
          <span
            className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10.5px] font-semibold"
            style={{ backgroundColor: t.errorBackground, color: t.errorText }}
          >
            <Lock size={10} /> {deny.length}
          </span>
        )}
      </div>

      {deny.length === 0 ? (
        <p className="text-[12.5px]" style={{ color: t.mutedText }}>
          No fields blocked — Zeph can write any field it's otherwise allowed to.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {deny.map((field) => (
            <span
              key={field}
              className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-md text-[12px] font-mono"
              style={{ backgroundColor: t.hoverBackground, color: t.text }}
            >
              {field}
              {!disabled && (
                <button type="button" onClick={() => onRemove(field)} style={{ color: t.mutedText }} aria-label={`Unblock ${field}`}>
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!disabled && (
        <button
          type="button"
          onClick={onManage}
          className="self-start flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-semibold transition-colors"
          style={{ backgroundColor: t.hoverBackground, color: t.text }}
        >
          <Plus size={13} /> Block a field
        </button>
      )}
    </div>
  )
}

const FieldPermissionsSection: React.FC<Props> = ({ fieldPermissions, onChange, disabled }) => {
  const [openType, setOpenType] = useState<CrmObjectType | null>(null)
  const openLabel = OBJECT_TYPES.find((o) => o.type === openType)?.label ?? ''

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {OBJECT_TYPES.map(({ type, label }) => {
          const deny = fieldPermissions[type]?.deny ?? []
          return (
            <ObjectTypeCard
              key={type}
              label={label}
              deny={deny}
              onRemove={(field) => onChange(type, deny.filter((f) => f !== field))}
              onManage={() => setOpenType(type)}
              {...(disabled ? { disabled } : {})}
            />
          )
        })}
      </div>

      <BlockFieldDialog
        objectType={openType}
        label={openLabel}
        deny={openType ? fieldPermissions[openType]?.deny ?? [] : []}
        onChange={(deny) => { if (openType) onChange(openType, deny) }}
        onClose={() => setOpenType(null)}
      />
    </>
  )
}

export default React.memo(FieldPermissionsSection)
